import React from "react";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import { Document, Page } from "react-pdf/dist/umd/entry.webpack";

import type { ContentProgressPdf } from "../../@types/portalAluno";
import MediaLoadingIndicator from "../MediaLoadingIndicator/MediaLoadingIndicator";
import "./styles.css";

interface PdfModuleViewerProps {
  title: string;
  pdf: ContentProgressPdf;
}

const MIN_ZOOM = 50;
const MAX_ZOOM = 200;
const ZOOM_STEP = 10;

const PdfModuleViewer = ({ title, pdf }: PdfModuleViewerProps) => {
  const initialTotalPages = Math.max(pdf.totalPages, 1);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [zoom, setZoom] = React.useState(100);
  const [totalPages, setTotalPages] = React.useState(initialTotalPages);
  const [availableWidth, setAvailableWidth] = React.useState(0);
  const documentContainerRef = React.useRef<HTMLDivElement>(null);
  const isAvailable = Boolean(pdf.sourceUrl);

  React.useEffect(() => {
    setCurrentPage(1);
    setZoom(100);
    setTotalPages(initialTotalPages);
  }, [initialTotalPages, pdf.sourceUrl, title]);

  React.useLayoutEffect(() => {
    const documentContainer = documentContainerRef.current;
    if (!documentContainer) return;

    const updateViewerSize = () => {
      const styles = window.getComputedStyle(documentContainer);
      const horizontalPadding =
        (parseFloat(styles.paddingLeft) || 0) +
        (parseFloat(styles.paddingRight) || 0);

      setAvailableWidth(
        Math.max(0, documentContainer.clientWidth - horizontalPadding)
      );
    };

    updateViewerSize();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateViewerSize);
      return () => window.removeEventListener("resize", updateViewerSize);
    }

    const observer = new ResizeObserver(updateViewerSize);
    observer.observe(documentContainer);
    return () => observer.disconnect();
  }, []);

  const sourceUrl = pdf.sourceUrl?.split("#")[0];
  const renderedPageWidth =
    availableWidth > 0
      ? Math.round(availableWidth * (zoom / 100))
      : undefined;

  return (
    <section
      className="pdf-module-viewer"
      aria-label={`Visualizador do módulo ${title}`}
    >
      <div
        className="pdf-module-viewer__toolbar"
        role="toolbar"
        aria-label="Controles do PDF"
      >
        <div className="pdf-module-viewer__download">
          {sourceUrl ? (
            <a href={sourceUrl} target="_blank" rel="noopener noreferrer">
              Download
            </a>
          ) : (
            <button type="button" disabled title="PDF ainda não disponível">
              Download
            </button>
          )}
        </div>

        <div className="pdf-module-viewer__pagination">
          <button
            type="button"
            onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
            disabled={currentPage === 1}
            aria-label="Página anterior"
          >
            <ChevronLeftRoundedIcon aria-hidden="true" />
          </button>
          <strong aria-live="polite">
            {currentPage} de {totalPages}
          </strong>
          <button
            type="button"
            onClick={() =>
              setCurrentPage((page) => Math.min(totalPages, page + 1))
            }
            disabled={currentPage === totalPages}
            aria-label="Próxima página"
          >
            <ChevronRightRoundedIcon aria-hidden="true" />
          </button>
        </div>

        <div className="pdf-module-viewer__zoom">
          <button
            type="button"
            onClick={() => setZoom((value) => Math.max(MIN_ZOOM, value - ZOOM_STEP))}
            disabled={zoom === MIN_ZOOM}
            aria-label="Diminuir zoom"
          >
            <RemoveRoundedIcon aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => setZoom((value) => Math.min(MAX_ZOOM, value + ZOOM_STEP))}
            disabled={zoom === MAX_ZOOM}
            aria-label="Aumentar zoom"
          >
            <AddRoundedIcon aria-hidden="true" />
          </button>
          <span className="sr-only" aria-live="polite">
            Zoom em {zoom}%
          </span>
        </div>
      </div>

      <div className="pdf-module-viewer__viewport">
        {isAvailable ? (
          <div
            ref={documentContainerRef}
            className="pdf-module-viewer__document"
          >
            <Document
              key={sourceUrl}
              file={sourceUrl}
              loading={
                <MediaLoadingIndicator
                  className="pdf-module-viewer__loading"
                  label="Carregando conteúdo"
                />
              }
              error={
                <div className="pdf-module-viewer__unavailable" role="alert">
                  <PictureAsPdfOutlinedIcon aria-hidden="true" />
                  <strong>Não foi possível carregar o PDF.</strong>
                </div>
              }
              onLoadSuccess={({ numPages }) => {
                const availablePages = Math.max(numPages, 1);
                setTotalPages(availablePages);
                setCurrentPage((page) => Math.min(page, availablePages));
              }}
            >
              <div
                className="pdf-module-viewer__page-shell"
                style={{
                  width: renderedPageWidth
                    ? `${renderedPageWidth}px`
                    : "100%",
                }}
              >
                <Page
                  key={currentPage}
                  pageNumber={currentPage}
                  width={renderedPageWidth}
                  loading={
                    <MediaLoadingIndicator
                      className="pdf-module-viewer__loading"
                      label="Carregando conteúdo"
                    />
                  }
                  error="Não foi possível renderizar esta página."
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                />
              </div>
            </Document>
          </div>
        ) : (
          <div className="pdf-module-viewer__unavailable" role="alert">
            <PictureAsPdfOutlinedIcon aria-hidden="true" />
            <strong>PDF não disponível no momento.</strong>
          </div>
        )}
      </div>
    </section>
  );
};

export default PdfModuleViewer;
